import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon,
  alpha,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useThemeMode } from "../../context/ThemeContext";
import { useCart } from "../../context/CartContext";
import { useFilters } from "../../context/FilterContext";
import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Favorite as FavoriteIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  LocalShipping as LocalShippingIcon,
  Group as GroupIcon,
  Payments as PaymentsIcon,
  ShoppingCart as ShoppingCartIcon,
  ShoppingBag as ShoppingBagIcon,
} from "@mui/icons-material";
import Badge from "@mui/material/Badge";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "../../assets/saraya_logo_new.png";
import LogoDark from "../../assets/saraya_logo_dark.png";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { cartItems } = useCart();
  const { filters, updateFilters } = useFilters();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    logout();
    navigate("/");
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleCountryChange = (newCountry) => {
    updateFilters({ ...filters, country: newCountry });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.about"), path: "/about" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  // Check if current path is active
  const isActivePath = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Mobile Drawer Content
  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box
          component="img"
          src={theme.palette.mode === "dark" ? LogoDark : Logo}
          alt="Saraya Auto Sales"
          sx={{
            height: 60,
            width: "auto",
            objectFit: "contain",
          }}
        />
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: "text.primary",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Links */}
      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
            <Button
              component={Link}
              to={item.path}
              fullWidth
              onClick={handleDrawerToggle}
              sx={{
                justifyContent: "flex-start",
                px: 3,
                py: 1.5,
                mx: 1,
                borderRadius: 2,
                color: isActivePath(item.path)
                  ? "primary.main"
                  : "text.primary",
                bgcolor: isActivePath(item.path)
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                fontWeight: isActivePath(item.path) ? 700 : 500,
                fontSize: "1rem",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: "inherit",
                  fontSize: "inherit",
                }}
              />
            </Button>
          </ListItem>
        ))}
      </List>

      {/* Bottom Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Theme & Language Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Theme Toggle Hidden 
            <IconButton
              onClick={toggleTheme}
              aria-label={t('nav.toggleTheme') || 'Toggle theme'}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                },
              }}
            >
              {mode === 'light' ? (
                <Brightness4Icon fontSize="small" />
              ) : (
                <Brightness7Icon fontSize="small" />
              )}
            </IconButton>
            */}

            <LanguageSwitcher />
          </Box>
        </Box>

        {/* Country Filter - Mobile Drawer */}
        {location.pathname === "/" && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel id="drawer-country-label">
              {t("filters.country")}
            </InputLabel>
            <Select
              labelId="drawer-country-label"
              value={filters.country}
              label={t("filters.country")}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              <MenuItem value="all">{t("filters.allCountries")}</MenuItem>
              <MenuItem value="US">
                {i18n.language === "fr" ? "États-Unis" : "United States"}
              </MenuItem>
              <MenuItem value="SN">Sénégal</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* User Actions */}
        {currentUser && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, px: 1 }}
            >
              {currentUser.email}
            </Typography>
            {currentUser.role === "admin" && (
              <>
                <Button
                  component={Link}
                  to="/admin/dashboard"
                  fullWidth
                  onClick={handleDrawerToggle}
                  startIcon={<DashboardIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderRadius: 2,
                    color: "text.primary",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {t("nav.adminDashboard")}
                </Button>
                <Button
                  component={Link}
                  to="/admin/orders"
                  fullWidth
                  onClick={handleDrawerToggle}
                  startIcon={<LocalShippingIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderRadius: 2,
                    color: isActivePath("/admin/orders")
                      ? "primary.main"
                      : "text.primary",
                    bgcolor: isActivePath("/admin/orders")
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    fontWeight: isActivePath("/admin/orders") ? 700 : 400,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  Gestion Suivis
                </Button>
                <Button
                  component={Link}
                  to="/admin/affiliations"
                  fullWidth
                  onClick={handleDrawerToggle}
                  startIcon={<PaymentsIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderRadius: 2,
                    color: isActivePath("/admin/affiliations")
                      ? "primary.main"
                      : "text.primary",
                    bgcolor: isActivePath("/admin/affiliations")
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    fontWeight: isActivePath("/admin/affiliations") ? 700 : 400,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  Gestion Commissions
                </Button>
              </>
            )}
            <Button
              component={Link}
              to="/profile"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={<PersonIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: "text.primary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {t("profile.title") || "Mon Profil"}
            </Button>
            <Button
              component={Link}
              to="/favorites"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={<FavoriteIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: "text.primary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {t("favorites.title") || "Mes Favoris"}
            </Button>
            <Button
              component={Link}
              to="/tracking"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={<LocalShippingIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: isActivePath("/tracking")
                  ? "primary.main"
                  : "text.primary",
                bgcolor: isActivePath("/tracking")
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                fontWeight: isActivePath("/tracking") ? 700 : 400,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              Suivi véhicule
            </Button>
            <Button
              component={Link}
              to="/purchases"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={<ShoppingBagIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: isActivePath("/purchases")
                  ? "primary.main"
                  : "text.primary",
                bgcolor: isActivePath("/purchases")
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                fontWeight: isActivePath("/purchases") ? 700 : 400,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {t("purchases.nav") || "Mes Achats"}
            </Button>
            <Button
              component={Link}
              to="/affiliations"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={<GroupIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: isActivePath("/affiliations")
                  ? "primary.main"
                  : "text.primary",
                bgcolor: isActivePath("/affiliations")
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                fontWeight: isActivePath("/affiliations") ? 700 : 400,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              Affiliation
            </Button>
            <Button
              fullWidth
              onClick={() => {
                handleDrawerToggle();
                handleLogoutClick();
              }}
              startIcon={<ExitToAppIcon />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                borderRadius: 2,
                color: "error.main",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              {t("nav.logout")}
            </Button>
          </>
        )}

        {/* Guest Actions */}
        {!currentUser && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              component={Link}
              to="/cart"
              fullWidth
              onClick={handleDrawerToggle}
              startIcon={
                <Badge badgeContent={cartItems.length} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              }
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1,
                mb: 1,
                borderRadius: 2,
                color: isActivePath("/cart") ? "primary.main" : "text.primary",
                bgcolor: isActivePath("/cart")
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                fontWeight: isActivePath("/cart") ? 700 : 400,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {t("cart.title") || "Mon Panier"}
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              fullWidth
              onClick={handleDrawerToggle}
              sx={{ borderRadius: 2 }}
            >
              {t("auth.login.submit") || "Se connecter"}
            </Button>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              fullWidth
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                background:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
              }}
            >
              {t("auth.register.submit") || "S'inscrire"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor:
          theme.palette.mode === "light"
            ? alpha(theme.palette.background.paper, 0.98)
            : alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: scrolled
          ? "0 10px 30px -10px rgba(0,0,0,0.1)"
          : "0 4px 12px rgba(0,0,0,0.03)",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        color: "text.primary",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 64, md: 72 },
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Desktop Logo */}
          <Box
            component={Link}
            to="/"
            aria-label={t("nav.homeDesc") || "Saraya Auto Sales Home"}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              textDecoration: "none",
              mr: 4,
              "&:hover": { opacity: 0.9 },
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === "dark" ? LogoDark : Logo}
              alt="Saraya Auto Sales"
              sx={{
                height: 70,
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label={t("nav.toggleMenu") || "Menu"}
              onClick={handleDrawerToggle}
              sx={{
                color: "text.primary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Mobile Logo - Centered */}
          <Box
            component={Link}
            to="/"
            aria-label={t("nav.homeDesc") || "Saraya Auto Sales Home"}
            sx={{
              display: { xs: "flex", md: "none" },
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === "dark" ? LogoDark : Logo}
              alt="Saraya Auto Sales"
              sx={{
                height: 50,
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Spacer for mobile */}
          <Box sx={{ width: 48, display: { xs: "flex", md: "none" } }} />

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flex: 1,
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {menuItems.map((item) => (
              <Button
                key={item.label}
                component={Link}
                to={item.path}
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  color: isActivePath(item.path)
                    ? "primary.main"
                    : theme.palette.mode === "light"
                      ? "secondary.main"
                      : "text.primary",
                  fontWeight: isActivePath(item.path) ? 700 : 500,
                  fontSize: "0.9375rem",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 6,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: isActivePath(item.path) ? "24px" : "0px",
                    height: "3px",
                    borderRadius: "2px",
                    bgcolor: "primary.main",
                    transition: "width 0.25s ease",
                  },
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    "&::after": {
                      width: "24px",
                    },
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Desktop Actions */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {/* Theme Toggle */}
            {/* Theme Toggle Hidden
            <Tooltip title={mode === 'light' ? 'Mode sombre' : 'Mode clair'}>
              <IconButton
                onClick={toggleTheme}
                aria-label={t('nav.toggleTheme') || 'Toggle theme'}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                  },
                }}
              >
                {mode === 'light' ? (
                  <Brightness4Icon fontSize="small" />
                ) : (
                  <Brightness7Icon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            */}

            {/* Country Filter - Only on Home Page */}
            {location.pathname === "/" && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="navbar-country-label">
                  {t("filters.country")}
                </InputLabel>
                <Select
                  labelId="navbar-country-label"
                  value={filters.country}
                  label={t("filters.country")}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  sx={{
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.divider, 0.3),
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <MenuItem value="all">{t("filters.allCountries")}</MenuItem>
                  <MenuItem value="US">
                    {i18n.language === "fr" ? "États-Unis" : "United States"}
                  </MenuItem>
                  <MenuItem value="SN">Sénégal</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Cart Icon */}
            <Tooltip title={t("cart.title") || "Panier"}>
              <IconButton
                component={Link}
                to="/cart"
                sx={{
                  color: isActivePath("/cart")
                    ? "primary.main"
                    : "text.primary",
                  bgcolor: isActivePath("/cart")
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                  },
                }}
              >
                <Badge badgeContent={cartItems.length} color="primary">
                  <ShoppingCartIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Menu */}
            {currentUser && (
              <Box sx={{ ml: 1 }}>
                <Tooltip title="Paramètres">
                  <IconButton
                    onClick={handleMenu}
                    sx={{
                      p: 0.5,
                      border: `2px solid ${alpha(
                        theme.palette.primary.main,
                        0.2,
                      )}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 32,
                        height: 32,
                        fontSize: "0.875rem",
                        fontWeight: 700,
                      }}
                    >
                      {currentUser.username
                        ? currentUser.username.charAt(0).toUpperCase()
                        : currentUser.email.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: 1 }}
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      minWidth: 200,
                      overflow: "visible",
                      filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.1))",
                      mt: 1,
                      "&::before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Connecté en tant que
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {currentUser.email}
                    </Typography>
                  </Box>
                  {currentUser.role === "admin" && (
                    <>
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          navigate("/admin/dashboard");
                        }}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemIcon>
                          <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        {t("nav.adminDashboard")}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          navigate("/admin/orders");
                        }}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemIcon>
                          <LocalShippingIcon fontSize="small" />
                        </ListItemIcon>
                        Gestion Suivis
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          navigate("/admin/affiliations");
                        }}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemIcon>
                          <PaymentsIcon fontSize="small" />
                        </ListItemIcon>
                        Gestion Commissions
                      </MenuItem>
                    </>
                  )}
                  <MenuItem
                    component={Link}
                    to="/favorites"
                    onClick={handleClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <FavoriteIcon fontSize="small" />
                    </ListItemIcon>
                    {t("favorites.title") || "Mes Favoris"}
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/tracking"
                    onClick={handleClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <LocalShippingIcon fontSize="small" />
                    </ListItemIcon>
                    Suivi véhicule
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/purchases"
                    onClick={handleClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <ShoppingBagIcon fontSize="small" />
                    </ListItemIcon>
                    {t("purchases.nav") || "Mes Achats"}
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/affiliations"
                    onClick={handleClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <GroupIcon fontSize="small" />
                    </ListItemIcon>
                    Affiliation
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/profile"
                    onClick={handleClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    {t("profile.title") || "Mon Profil"}
                  </MenuItem>
                  <MenuItem
                    onClick={handleLogoutClick}
                    sx={{ py: 1.5, color: "error.main" }}
                  >
                    <ListItemIcon>
                      <ExitToAppIcon
                        fontSize="small"
                        sx={{ color: "error.main" }}
                      />
                    </ListItemIcon>
                    {t("nav.logout")}
                  </MenuItem>
                </Menu>
              </Box>
            )}
            {/* Guest Menu Desktop */}
            {!currentUser && (
              <Box sx={{ display: "flex", gap: 1.5, ml: 2 }}>
                <Button
                  component={Link}
                  to="/login"
                  variant="text"
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {t("auth.login.submit")}
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {t("auth.register.submit")}
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            borderRight: "none",
            boxShadow: "4px 0 24px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 320,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("nav.logoutConfirmTitle") || "Confirmer la déconnexion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("nav.logoutConfirmMessage") ||
              "Êtes-vous sûr de vouloir vous déconnecter ?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLogoutCancel} color="inherit">
            {t("common.cancel") || "Annuler"}
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            autoFocus
          >
            {t("nav.logout") || "Déconnexion"}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;
